import type { GridHostedCellMount } from '@thinkalpha/ui-table-svelte/svelteCellHost.svelte';
import type { SuggestedColumnsKey, TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import type { ReactContainer } from 'src/ReactContainer';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'clientColumns' });

/**
 * Definition for a client-side column that can be registered in the client column registry.
 * Client columns are columns whose data is provided by the client rather than the server.
 */
export interface ClientColumnDefinition {
    /** Unique key identifying this client column type (e.g., 'notes') */
    clientKey: string;
    /** Display name shown in the UI (e.g., 'Notes') */
    displayName: string;
    /**
     * Factory for the column's hosted grid cell. Receives the resolution params and returns the
     * Svelte component with its static per-column props.
     */
    createGridCell: (params: ClientColumnRendererParams) => GridHostedCellMount;
    /**
     * The table key this column is available in. Only tables whose suggestedColumnsKey
     * matches will show this column as a suggestion.
     * To support the same column type in multiple table contexts (with different requiredSlugs),
     * register separate definitions with distinct clientKeys.
     */
    tableKey: SuggestedColumnsKey;
    /** Factory function to create a new column of this type */
    createColumn: () => TemplateColumn;
    /**
     * Optional list of slugs this column requires resolved to field IDs.
     * hostedColumnCells resolves these from the table's resolved columns
     * and provides them in ClientColumnRendererParams.resolvedFieldIds.
     */
    requiredSlugs?: string[];
}

/**
 * Parameters passed to the grid cell factory
 */
export interface ClientColumnRendererParams {
    /** The IoC container for resolving services */
    container: ReactContainer;
    /**
     * Map of slug to resolved field ID for columns that specify requiredSlugs.
     * hostedColumnCells resolves these from the table's resolved columns
     * and from the symbolIdField prop (for Slugs.symbol).
     */
    resolvedFieldIds?: Record<string, string>;
}

/**
 * Registry mapping clientKey to ClientColumnDefinition.
 * Client columns register themselves here so the table system can look them up.
 */
const clientColumnRegistry = new Map<string, ClientColumnDefinition>();

/**
 * Register a client column definition in the registry.
 * This should be called during application initialization.
 */
export function registerClientColumn(definition: ClientColumnDefinition): void {
    if (clientColumnRegistry.has(definition.clientKey)) {
        log.warn(`Client column with key '${definition.clientKey}' is already registered. Overwriting.`);
    }
    clientColumnRegistry.set(definition.clientKey, definition);
}

/**
 * Get a client column definition by its key.
 * @returns The definition, or undefined if not found
 */
export function getClientColumnDefinition(clientKey: string): ClientColumnDefinition | undefined {
    return clientColumnRegistry.get(clientKey);
}

/**
 * Get all available client columns, filtered by the table's suggested columns key.
 * Columns are included only when definition.tableKey matches the provided table key.
 */
export function getAvailableClientColumns(tableKey?: SuggestedColumnsKey): ClientColumnDefinition[] {
    return Array.from(clientColumnRegistry.values()).filter((definition) => definition.tableKey === tableKey);
}

/**
 * Check if a column is a registered client column.
 */
export function isRegisteredClientColumn(clientKey: string): boolean {
    return clientColumnRegistry.has(clientKey);
}
